<template>
  <div ref="root" :class="$style['menu']" @keydown.esc="close(true)" @focusout="onFocusOut">
    <button
      ref="trigger"
      type="button"
      :class="[$style['menu__trigger'], open && $style['menu__trigger--open'], count && $style['menu__trigger--active']]"
      :aria-expanded="open"
      :aria-controls="id"
      @click="open ? close() : (open = true)">
      {{ label }}
      <span v-if="count" :class="$style['menu__count']">{{ count }}</span>
      <Icon name="ChevronDown" :class="$style['menu__chevron']" />
    </button>
    <div v-show="open" :id="id" :class="[$style['menu__panel'], align === 'end' && $style['menu__panel--end']]" role="group" :aria-label="label">
      <slot />
    </div>
  </div>
</template>

<script setup lang="ts">
// A button that opens a panel under it, for filters and the like. A click next to it, Escape
// or tabbing away closes it; Escape puts the keyboard back on the button.
withDefaults(defineProps<{
  label: string
  // How many are chosen inside, shown on the button
  count?: number
  align?: 'start' | 'end'
}>(), { count: 0, align: 'start' })

const id = useId()
const open = ref(false)
const root = ref<HTMLElement>()
const trigger = ref<HTMLButtonElement>()

function close (returnFocus = false) {
  if (!open.value) { return }
  open.value = false
  if (returnFocus) { trigger.value?.focus() }
}

function onFocusOut (event: FocusEvent) {
  const next = event.relatedTarget as Node | null
  if (next && !root.value?.contains(next)) { close() }
}

function onPointerDown (event: PointerEvent) {
  if (!root.value?.contains(event.target as Node)) { close() }
}

watch(open, isOpen => {
  if (isOpen) {
    document.addEventListener('pointerdown', onPointerDown)
  } else {
    document.removeEventListener('pointerdown', onPointerDown)
  }
})

onBeforeUnmount(() => document.removeEventListener('pointerdown', onPointerDown))
defineExpose({ close })
</script>

<style module>
.menu {
  position: relative;
}

.menu__trigger {
  display: inline-flex;
  align-items: center;
  gap: var(--spacing);
  min-height: 2.5rem;
  padding: 0 var(--spacing-m) 0 var(--spacing-l);
  border: 0;
  border-radius: 999rem;
  background: var(--opacity-surface);
  color: var(--text-base);
  font: inherit;
  font-size: var(--font-size-small);
  cursor: pointer;
  transition: background-color 150ms;
}

.menu__trigger:hover,
.menu__trigger--open {
  background: var(--opacity-surface-state-2);
}

.menu__trigger:focus-visible {
  outline: 0.125rem solid var(--text-base);
  outline-offset: 0.125rem;
}

/* Something chosen: the amber count says so without opening it */
.menu__count {
  display: inline-grid;
  place-items: center;
  min-width: 1.25rem;
  height: 1.25rem;
  padding-inline: var(--spacing-s);
  border-radius: 999rem;
  background: var(--primary-color);
  color: var(--stage-black);
  font-size: 0.75rem;
  font-weight: var(--font-weight-bold);
  font-variant-numeric: tabular-nums;
}

.menu__chevron {
  width: 1rem;
  height: 1rem;
  color: var(--text-subtle);
  transition: transform 200ms var(--stage-ease);
}

.menu__trigger--open .menu__chevron {
  transform: rotate(180deg);
}

.menu__panel {
  position: absolute;
  top: calc(100% + var(--spacing));
  left: 0;
  z-index: 30;
  width: max-content;
  max-width: min(40rem, 100vw - 2 * var(--stage-gutter));
  max-height: min(70svh, 32rem);
  padding: var(--spacing-l);
  overflow-y: auto;
  overscroll-behavior: contain;
  border: var(--stage-hairline) solid var(--stage-line);
  background: oklch(12% 0 0deg);
  box-shadow: 0 1.5rem 3rem oklch(0% 0 0deg / 60%);
  animation: menu-in 160ms ease-out;
}

.menu__panel--end {
  right: 0;
  left: auto;
}

@keyframes menu-in {
  from {
    opacity: 0;
    transform: translateY(calc(-1 * var(--spacing)));
  }
}

@media (prefers-reduced-motion: reduce) {
  .menu__panel {
    animation: none;
  }

  .menu__chevron {
    transition: none;
  }
}
</style>
